import { Injectable } from '@angular/core';
import { Pagination } from '@deckplan.io/common/records';
import { CardSymbol, Query } from '@deckplan.io/common';
import { UrlUtils } from '../common/utils/url-utils';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Mockable } from '../mocks/mockable.decorator';

export interface CardSymbolsAndTotalCount {
    totalCount: number;
    cardSymbols: CardSymbol[];
}

@Injectable({
    providedIn: 'root'
})
@Mockable
export class CardSymbolService {

    private readonly backendUrl = UrlUtils.constructBackendUrl('card-symbols');

    private readonly symbolCache = new Map();

    constructor(private readonly http: HttpClient) {
        // Set up a cache for us to retrieve individual symbols with; since we use so many of them, querying them
        // individually would be prohibitively expensive
        this.getAll().subscribe((cardsAndTotal) => {
            for (const cardSymbol of cardsAndTotal.cardSymbols) {
                this.symbolCache.set(cardSymbol.symbol, cardSymbol);
            }
        });
    }

    public getAll(pagination: Partial<Pagination> = null, query: Query = null): Observable<CardSymbolsAndTotalCount> {
        const url = UrlUtils.constructPaginationAndFilterUrl(this.backendUrl, pagination, query);

        return this.http.get(url, {observe: 'response'})
            .pipe(
                map(res => ({totalCount: parseInt(res.headers.get('total-count'), 10), cardSymbols: res.body as any}))
            );
    }

    public getOne(symbol: string): Observable<CardSymbol> {
        // To make things easier, let's detect if the symbol already has { and } around it, and if not, add them.
        if (symbol[0] !== '{') {
            symbol = `{${symbol}`;
        }

        if (symbol[symbol.length - 1] !== '}') {
            symbol = `${symbol}}`;
        }

        return of(this.symbolCache.get(symbol) as CardSymbol);
    }
}
